import React, { useContext, useState, useMemo, useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
  Alert,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  ButtonGroup,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import {
  showAllValidationErrorsAtom,
  validatingExtractionIdsAtom,
  validationNotReadyExtractionIdsAtom,
} from '../../atoms/spreadsheet-atoms';
import {
  deriveSettledExtractionIds,
  mergeValidationFetchStates,
  useRetryValidation,
  useValidationFetchState,
} from '../hooks/useValidationFetchState';
import { MarkStateContext, MarkDispatchContext } from '../../context/MarkContext';
import { useQueryClient } from '@tanstack/react-query';
import { useDataValidation } from '../hooks/useDataValidation';
import { buildValidationSummaryByExtractionId } from '../../utils/validationUtils';

const ViewAllValidationErrors: React.FC = () => {
  const [showAllValidationErrors, setShowAllValidationErrors] = useAtom(
    showAllValidationErrorsAtom
  );
  const dispatch = useContext(MarkDispatchContext);
  const { validationSummaryByExtractionId, validationsEnabled, visibleExtractionIds } =
    useContext(MarkStateContext);
  const [datasetFilter, setDatasetFilter] = useState<string>('all');
  const [showDismissed, setShowDismissed] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const [includeTransformed, setIncludeTransformed] = useState<boolean>(false);
  const extractedValidatingIds = useAtomValue(validatingExtractionIdsAtom);
  const extractedNotReadyIds = useAtomValue(validationNotReadyExtractionIdsAtom);
  const retryValidation = useRetryValidation();

  // Fetch validation with transformed=true when checkbox is checked
  // The context now MERGES results, so transformed data is added to existing extracted data
  const transformedActive = validationsEnabled && includeTransformed;
  const transformedValidationQueries = useDataValidation(
    transformedActive,
    visibleExtractionIds,
    true // includeTransformed
  );

  const isLoading: boolean = transformedValidationQueries.some((result: any) => result.isLoading);
  const isFetching: boolean = transformedValidationQueries.some((result: any) => result.isFetching);
  const refreshLoading: boolean = includeTransformed && (isLoading || isFetching);

  // The dialog's own transformed queries poll independently of the review page's extracted
  // ones, so their fetch state joins the page's when deciding what is stale or retryable.
  // A disabled query keeps its cached error but can neither poll nor be refetched, so it
  // only counts while the transformed source is active.
  const transformedFetchState = useValidationFetchState(
    transformedActive ? transformedValidationQueries : [],
    visibleExtractionIds
  );
  const { validatingExtractionIds, notReadyExtractionIds: validationNotReadyExtractionIds } =
    useMemo(
      () =>
        mergeValidationFetchStates(
          {
            validatingExtractionIds: extractedValidatingIds,
            notReadyExtractionIds: extractedNotReadyIds,
          },
          transformedFetchState
        ),
      [extractedValidatingIds, extractedNotReadyIds, transformedFetchState]
    );

  // Each extraction's transformed result is merged into context as soon as its own query
  // has settled, so a finished extraction is not held back by one still polling.
  const settledTransformedIds = deriveSettledExtractionIds(
    transformedValidationQueries,
    visibleExtractionIds
  );
  const settledTransformedKey = settledTransformedIds.join('|');
  useEffect(() => {
    if (includeTransformed && settledTransformedIds.length > 0) {
      const summary = buildValidationSummaryByExtractionId(
        settledTransformedIds,
        transformedValidationQueries
      );
      dispatch({
        type: 'SET_VALIDATION_SUMMARY_BY_EXTRACTION_ID',
        payload: summary,
        datasetTypesToClear: ['transformed'],
      });
    }
  }, [
    includeTransformed,
    settledTransformedKey,
    JSON.stringify(transformedValidationQueries.map((q: any) => q.data)),
    JSON.stringify(visibleExtractionIds),
  ]);

  const handleIncludeTransformedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setIncludeTransformed(newValue);
    if (newValue) {
      // Invalidate transformed validation queries to force refetch
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          // Match queries with includeTransformed=true (position 3 in query key)
          return (
            Array.isArray(key) &&
            key[0] === 'extraction-validation' &&
            key[3] === true
          );
        },
      });
    }
  };

  const handleClose = () => {
    setShowAllValidationErrors(false);
  };

  // Extractions whose validation is being refetched, or whose poll ran out, have a stale
  // summary: their rows are replaced by the status banner above the table.
  const staleExtractionIds = useMemo(
    () => new Set([...validatingExtractionIds, ...validationNotReadyExtractionIds]),
    [validatingExtractionIds, validationNotReadyExtractionIds]
  );

  // Get all validation errors from all extractions
  const allValidationErrors = validationSummaryByExtractionId
    .filter((extraction) => !staleExtractionIds.has(extraction.extractionId))
    .flatMap((extraction) =>
      extraction.validationErrorDetails.map((error) => ({
        extractionId: extraction.extractionId,
        ...error,
      }))
    );

  // Get unique dataset types from validation errors
  const uniqueDatasetTypes = useMemo(() => {
    const datasets = allValidationErrors
      .map((error) => error.dataset)
      .filter((dataset) => dataset && dataset !== 'N/A');
    return [...new Set(datasets)];
  }, [allValidationErrors]);

  const showFilters =
    uniqueDatasetTypes.includes('transformed') || uniqueDatasetTypes.includes('schematized');

  // Filter validation errors based on dataset filter and dismissed status
  const filteredValidationErrors = useMemo(() => {
    let filtered = allValidationErrors;

    // Filter by dismissed status
    if (!showDismissed) {
      filtered = filtered.filter((error) => !error.dismissalReason);
    }

    // Filter by dataset
    if (datasetFilter !== 'all') {
      filtered = filtered.filter((error) => error.dataset === datasetFilter);
    }

    return filtered;
  }, [allValidationErrors, datasetFilter, showDismissed]);

  return (
    <DialogGlynt
      open={showAllValidationErrors}
      closeModal={handleClose}
      cypressId="validation-errors-dialog"
      width="900px"
    >
      <DialogTitleGlynt title="All Validation Errors" closeModal={handleClose} />

      <Box sx={{ mb: 2, px: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: showFilters ? 'space-between' : 'flex-end',
          }}
        >
          {showFilters && (
            <ButtonGroup size="small" variant="outlined">
              <Button
                variant={datasetFilter === 'all' ? 'contained' : 'outlined'}
                onClick={() => setDatasetFilter('all')}
                data-cy="filter-all"
              >
                All
              </Button>
              {uniqueDatasetTypes.includes('extracted') && (
                <Button
                  variant={datasetFilter === 'extracted' ? 'contained' : 'outlined'}
                  onClick={() => setDatasetFilter('extracted')}
                  data-cy="filter-extracted"
                >
                  Extracted
                </Button>
              )}
              {uniqueDatasetTypes.includes('transformed') && (
                <Button
                  variant={datasetFilter === 'transformed' ? 'contained' : 'outlined'}
                  onClick={() => setDatasetFilter('transformed')}
                  data-cy="filter-transformed"
                >
                  Transformed
                </Button>
              )}
              {uniqueDatasetTypes.includes('schematized') && (
                <Button
                  variant={datasetFilter === 'schematized' ? 'contained' : 'outlined'}
                  onClick={() => setDatasetFilter('schematized')}
                  data-cy="filter-schematized"
                >
                  Schematized
                </Button>
              )}
            </ButtonGroup>
          )}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showDismissed}
                  onChange={(e) => setShowDismissed(e.target.checked)}
                  data-cy="checkbox-show-dismissed"
                />
              }
              label="Show Dismissed"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!includeTransformed}
                  onChange={handleIncludeTransformedChange}
                  disabled={refreshLoading}
                  data-cy="checkbox-include-transformed"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>View Transformed</span>
                  {refreshLoading && <CircularProgress size={14} />}
                </Box>
              }
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 2, flex: 1, maxHeight: '60vh', overflow: 'auto' }}>
        {validatingExtractionIds.length > 0 && (
          <Alert
            severity="info"
            icon={<CircularProgress size={16} />}
            sx={{ mb: 2 }}
            data-cy="validation-errors-validating"
          >
            Validating {validatingExtractionIds.length} extraction
            {validatingExtractionIds.length === 1 ? '' : 's'}: {validatingExtractionIds.join(', ')}
          </Alert>
        )}
        {validationNotReadyExtractionIds.length > 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            data-cy="validation-errors-not-ready"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => validationNotReadyExtractionIds.forEach((id) => retryValidation(id))}
                data-cy="btn-validation-errors-retry"
              >
                Retry
              </Button>
            }
          >
            Validation did not finish in time for {validationNotReadyExtractionIds.length}{' '}
            extraction{validationNotReadyExtractionIds.length === 1 ? '' : 's'}:{' '}
            {validationNotReadyExtractionIds.join(', ')}
          </Alert>
        )}
        {allValidationErrors.length === 0 ? (
          <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
            No validation errors found.
          </Typography>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ maxHeight: '50vh' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="12%" align="left">
                      Extraction ID
                    </TableCell>
                    <TableCell width="12%" align="left">
                      Field
                    </TableCell>
                    <TableCell width="8%" align="center">
                      Type
                    </TableCell>
                    <TableCell width="35%" align="left">
                      Error Details
                    </TableCell>
                    <TableCell width="10%" align="left">
                      Dataset
                    </TableCell>
                    {showDismissed && (
                      <TableCell width="23%" align="left">
                        Dismissal Reason
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredValidationErrors.map((error, index) => (
                    <TableRow key={index} data-cy={`validation-error-row-${index}`}>
                      <TableCell align="left">
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'medium', color: 'primary.main' }}
                        >
                          {error.extractionId}
                        </Typography>
                      </TableCell>
                      <TableCell align="left">
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {error.field}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={error.type} size="small" color="warning" variant="outlined" />
                      </TableCell>
                      <TableCell align="left">
                        <Box>
                          {error.error && (
                            <Typography variant="body2" color="error" sx={{ mb: 0.5 }}>
                              {error.error}
                            </Typography>
                          )}
                          {error.errorDetail && (
                            <Typography variant="body2" color="text.secondary">
                              {error.errorDetail}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="left">
                        <Typography variant="body2" color="text.secondary">
                          {error.dataset || 'N/A'}
                        </Typography>
                      </TableCell>
                      {showDismissed && (
                        <TableCell align="left">
                          {error.dismissalReason ? (
                            <Chip
                              label={error.dismissalReason}
                              size="small"
                              color="default"
                              variant="filled"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>

      <DialogActionsGlynt
        handleSubmit={handleClose}
        closeModal={handleClose}
        showClearButton={false}
        cancelCypressId="btn-validation-errors-cancel"
        submitCypressId="btn-validation-errors-close"
        clearCypressId="btn-validation-errors-clear"
        submitLoading={false}
        showCancelButton={false}
        submitButtonText="Close"
      />
    </DialogGlynt>
  );
};

export default ViewAllValidationErrors;
